import React, { useState, useEffect, useRef } from 'react';
import { SplashScreen } from './SplashScreen';
import { SimpleLoader } from './SimpleLoader';
import { logDiag } from '@/utils/appDiagnostics';

interface AppRenderControllerProps {
  children: React.ReactNode;
  isInitializing: boolean;
  isComplete: boolean;
  /** Estado único de `useAppInitialization` no App — evita segunda instância do hook na SplashScreen */
  progress: number;
  currentStep: string;
  session: any;
  authLoading: boolean;
  onInitializationComplete?: () => void;
  onAppFullyLoaded?: () => void; // Nova prop para notificar quando app estiver totalmente carregado
}

export const AppRenderController: React.FC<AppRenderControllerProps> = ({ 
  children, 
  isInitializing,
  isComplete,
  progress,
  currentStep,
  session,
  authLoading,
  onInitializationComplete,
  onAppFullyLoaded
}) => {
  // Usar window.location.pathname como fallback quando não estiver no contexto do Router
  const [currentPath, setCurrentPath] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.location.pathname;
    }
    return '/';
  });

  // Atualizar pathname quando a rota mudar (usando popstate e pushstate)
  useEffect(() => {
    const updatePath = () => {
      if (typeof window !== 'undefined') {
        setCurrentPath(window.location.pathname);
      }
    };

    // Escutar mudanças de rota
    window.addEventListener('popstate', updatePath);
    
    // Interceptar pushState e replaceState
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      updatePath();
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      updatePath();
    };

    return () => {
      window.removeEventListener('popstate', updatePath);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);
  const [appFullyLoaded, setAppFullyLoaded] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const mountedRef = useRef(false);
  const delayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);
  const hasEverCompletedRef = useRef(false);
  const wasHiddenRef = useRef(false);
  const pageLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionRef = useRef(session);
  const isCompleteRef = useRef(isComplete);
  const onAppFullyLoadedRef = useRef(onAppFullyLoaded);
  const currentPathRef = useRef(currentPath);
  const appFullyLoadedRef = useRef(appFullyLoaded);
  const pageLoadingRef = useRef(pageLoading);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    isCompleteRef.current = isComplete;
  }, [isComplete]);

  useEffect(() => {
    onAppFullyLoadedRef.current = onAppFullyLoaded;
  }, [onAppFullyLoaded]);

  useEffect(() => {
    currentPathRef.current = currentPath;
  }, [currentPath]);

  useEffect(() => {
    appFullyLoadedRef.current = appFullyLoaded;
  }, [appFullyLoaded]);

  useEffect(() => {
    pageLoadingRef.current = pageLoading;
  }, [pageLoading]);
  // Mapeamento de rotas para mensagens personalizadas
  const getPageMessage = (pathname: string): string | undefined => {
    const routeMessages: Record<string, string> = {
      '/settings': 'Carregando configurações...',
      '/dashboard': 'Carregando dashboard...',
      '/leads': 'Carregando leads...',
      '/companies': 'Carregando empresas...',
      '/deals': 'Carregando negócios...',
      '/deals/list': 'Carregando lista de negócios...',
      '/deals/kanban': 'Carregando kanban...',
      '/sequences': 'Carregando cadências...',
      '/activities': 'Carregando atividades...',
      '/chat': 'Carregando chat...',
      '/email': 'Carregando emails...',
      '/analytics': 'Carregando análises...',
      '/admin': 'Carregando administração...',
      '/profile': 'Carregando perfil...',
    };

    // Verificar rota exata primeiro
    if (routeMessages[pathname]) {
      return routeMessages[pathname];
    }

    // Verificar rotas que começam com o pathname
    for (const [route, message] of Object.entries(routeMessages)) {
      if (pathname.startsWith(route) && route !== '/') {
        return message;
      }
    }

    return undefined;
  };

  const customMessage = getPageMessage(currentPath);

  // Detectar mudança de rota e resetar estado de carregamento
  useEffect(() => {
    // Após primeira carga completa, não desmontar páginas ao trocar de rota
    const anyWindow = window as any;
    const hasEverCompleted = hasEverCompletedRef.current || anyWindow.__appEverCompleted;
    if (hasEverCompleted) {
      setPageLoading(false);
      setAppFullyLoaded(true);
      hasInitializedRef.current = true;
      return () => {
        if (pageLoadTimeoutRef.current) {
          clearTimeout(pageLoadTimeoutRef.current);
        }
      };
    }

    // Primeira carga mantém o comportamento de splash controlado
    setPageLoading(true);
    setAppFullyLoaded(false);
    hasInitializedRef.current = false;

    if (pageLoadTimeoutRef.current) {
      clearTimeout(pageLoadTimeoutRef.current);
    }

    pageLoadTimeoutRef.current = setTimeout(() => {
      setPageLoading(false);
    }, 1500);

    return () => {
      if (pageLoadTimeoutRef.current) {
        clearTimeout(pageLoadTimeoutRef.current);
      }
    };
  }, [currentPath]);

  // Detectar quando o usuário perde/retorna foco
  // IMPORTANTE: NÃO alterar estados aqui para evitar remontagem de componentes
  // Apenas usar refs para tracking interno
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      wasHiddenRef.current = !isVisible;

      // Se a app já completou a inicialização uma vez, marcar a flag global
      // Isso evita que a splash apareça novamente ao trocar de aba
      if (isVisible && hasEverCompletedRef.current) {
        (window as any).__appEverCompleted = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const hasEverCompleted = hasEverCompletedRef.current || (window as any).__appEverCompleted;
  // Calcular shouldShowSplash ANTES de qualquer return condicional
  // Mostrar splash se: está inicializando, não completou, não carregou app, ou página ainda está carregando
  const shouldShowSplash = session && (isInitializing || !isComplete || !appFullyLoaded || pageLoading) &&
    !hasEverCompleted;

  useEffect(() => {
    mountedRef.current = true;

    // Limpar timeout anterior se houver
    if (delayTimeoutRef.current) {
      clearTimeout(delayTimeoutRef.current);
      delayTimeoutRef.current = null;
    }

    // Se a inicialização foi completada E a página terminou de carregar,
    // iniciar delay curto antes de sair da splash screen
    // IMPORTANTE: Processar sempre que isComplete for true, appFullyLoaded for false E pageLoading for false
    // Isso garante que funcione tanto na primeira carga quanto ao voltar para a guia
    if (isComplete && !appFullyLoaded && !pageLoading) {
      // Delay mínimo para garantir que todos os estados estejam estáveis
      // e dar tempo para hooks das páginas iniciarem seus carregamentos
      delayTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          setAppFullyLoaded(true);
          hasInitializedRef.current = true;
          hasEverCompletedRef.current = true;
          (window as any).__appEverCompleted = true;
          onInitializationComplete?.();
          onAppFullyLoaded?.(); // Notifica que o app está totalmente carregado
        }
      }, 300); // Reduzido para 300ms já que pageLoading já aguardou 1500ms
    }

    // Se isComplete mudar para false (reinicialização), resetar flags
    // MAS apenas se nunca completou com sucesso — após primeira conclusão,
    // splash NUNCA reaparece para evitar destruir estado de componentes montados.
    if (!isComplete && hasInitializedRef.current && !hasEverCompletedRef.current) {
      logDiag('APP_RENDER', 'isComplete went false before first completion — re-splashing');
      hasInitializedRef.current = false;
      setAppFullyLoaded(false);
    } else if (!isComplete && hasInitializedRef.current && hasEverCompletedRef.current) {
      logDiag('APP_RENDER', 'isComplete went false AFTER completion — ignoring (latch)');
    }

    return () => {
      mountedRef.current = false;
      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current);
        delayTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, appFullyLoaded, pageLoading]); // Incluir pageLoading para reagir quando página carregar


  // Mostrar loader simples se ainda está carregando autenticação (antes de existir sessão)
  if (authLoading && !session) {
    return <SimpleLoader message="Verificando autenticação..." />;
  }

  // Renderizar o conteúdo SEM desmontar, e apenas sobrepor loaders quando necessário
  // Ocultar children quando splash está ativa para evitar renderização de páginas por baixo
  return (
    <div className="relative h-full w-full">
      <div className={shouldShowSplash ? 'opacity-0 pointer-events-none' : ''}>
        {children}
      </div>
      {authLoading && session && (
        <div className="absolute inset-0 z-50">
          <SimpleLoader message="Verificando autenticação..." />
        </div>
      )}
      {shouldShowSplash && (
        <div className="absolute inset-0 z-50 bg-white">
          <SplashScreen
            progress={progress}
            currentStep={currentStep}
            isComplete={isComplete}
            customMessage={customMessage}
          />
        </div>
      )}
    </div>
  );
};

